import React from 'react';
import { 
  syncRadioChannels, 
  addRadioChannelWithCacheClear, 
  deleteRadioChannelWithCacheClear,
  syncLocations,
  addLocationWithCacheClear,
  deleteLocationWithCacheClear,
  syncTasks,
  addTaskWithCacheClear,
  deleteTaskWithCacheClear,
  syncFeedbacks,
  addFeedbackWithCacheClear,
  deleteFeedbackWithCacheClear,
  syncOperationsTypes,
  syncAllOperationsTypes,
  addOperationsTypeWithCacheClear,
  deleteOperationsTypeWithCacheClear,
  clearCache
} from '../utils/syncThrottled';
import { settingsAPI } from '../services/api';

interface GlobalSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgID: number;
}

const GlobalSettingsModal: React.FC<GlobalSettingsModalProps> = ({ isOpen, onClose, orgID }) => {
  const effectiveOrgID = React.useMemo(() => {
    // 1) Bevorzugt explizit ausgewählte Organisation
    const selectedOrgID = localStorage.getItem('selectedOrgID') || sessionStorage.getItem('selectedOrgID');
    if (selectedOrgID) {
      const parsed = parseInt(selectedOrgID, 10);
      if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    }

    // 2) Prop aus Parent
    if (typeof orgID === 'number' && orgID > 0) return orgID;

    // 3) Fallback auf Token
    try {
      const accessToken = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (accessToken) {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        if (typeof payload.orgID === 'number' && payload.orgID > 0) {
          return payload.orgID;
        }
      }
    } catch {}

    return 0;
  }, [orgID]);

  // State für maximale Trupps
  const [maxTrupps, setMaxTrupps] = React.useState(() => {
    const saved = localStorage.getItem('maxTrupps');
    return saved ? parseInt(saved) : 10;
  });

  // State für Schnell-Erstellung
  const [quickTruppCreation, setQuickTruppCreation] = React.useState(() => {
    const saved = localStorage.getItem('quickTruppCreation');
    return saved === 'true';
  });
  const [quickTruppCreationLoading, setQuickTruppCreationLoading] = React.useState(false);
  // State für Truppnamen-Modus
  const [truppNamingMode, setTruppNamingMode] = React.useState<'leader' | 'number'>(() => {
    const saved = localStorage.getItem('truppNamingMode');
    return saved === 'number' ? 'number' : 'leader';
  });
  const [truppNamingModeLoading, setTruppNamingModeLoading] = React.useState(false);

  // State für Standard-Tasks
  const [standardTasks, setStandardTasks] = React.useState<string[]>([]);
  const [tasksWithIds, setTasksWithIds] = React.useState<any[]>([]);
  const [newTask, setNewTask] = React.useState('');

  // State für Standard-Standorte
  const [standardStandorte, setStandardStandorte] = React.useState<string[]>([]);
  const [locationsWithIds, setLocationsWithIds] = React.useState<any[]>([]);
  const [newStandort, setNewStandort] = React.useState('');

  // State für Standard-Funkkanäle
  const [standardFunkkanaele, setStandardFunkkanaele] = React.useState<string[]>([]);
  const [radioChannelsWithIds, setRadioChannelsWithIds] = React.useState<any[]>([]);
  const [newFunkkanal, setNewFunkkanal] = React.useState('');

  // State für Standard-Feedbacks
  const [standardFeedbacks, setStandardFeedbacks] = React.useState<string[]>([]);
  const [feedbacksWithIds, setFeedbacksWithIds] = React.useState<any[]>([]);
  const [newFeedback, setNewFeedback] = React.useState('');

  // State für Operations Types
  const [operationsTypes, setOperationsTypes] = React.useState<any[]>([]);
  const [allOperationsTypes, setAllOperationsTypes] = React.useState<any[]>([]);

  // Loading states für Buttons
  const [deletingTaskIds, setDeletingTaskIds] = React.useState<Set<number>>(new Set());
  const [deletingLocationIds, setDeletingLocationIds] = React.useState<Set<number>>(new Set());
  const [deletingRadioChannelIds, setDeletingRadioChannelIds] = React.useState<Set<number>>(new Set());
  const [deletingFeedbackIds, setDeletingFeedbackIds] = React.useState<Set<number>>(new Set());
  const [deletingOperationsTypeIds, setDeletingOperationsTypeIds] = React.useState<Set<number>>(new Set());

  // Ref to track if data has been loaded
  const dataLoadedRef = React.useRef(false);

  // Load quick trupp creation setting from API
  React.useEffect(() => {
    const loadQuickTruppSetting = async () => {
      if (!isOpen || !effectiveOrgID) return;
      
      try {
        setQuickTruppCreationLoading(true);
        const response = await settingsAPI.getQuickTruppCreation(effectiveOrgID);
        const enabled = response?.enabled === true;
        setQuickTruppCreation(enabled);
        localStorage.setItem(`quickTruppCreation_${effectiveOrgID}`, enabled.toString());
        localStorage.setItem('quickTruppCreation', enabled.toString());
      } catch (error) {
        console.warn('Failed to load quick trupp creation setting:', error);
        // Fallback: org-spezifischer Cache, danach globaler Cache
        const savedOrg = localStorage.getItem(`quickTruppCreation_${effectiveOrgID}`);
        const savedGlobal = localStorage.getItem('quickTruppCreation');
        setQuickTruppCreation((savedOrg ?? savedGlobal) === 'true');
      } finally {
        setQuickTruppCreationLoading(false);
      }
    };
    
    loadQuickTruppSetting();
  }, [isOpen, effectiveOrgID]);

  React.useEffect(() => {
    const loadTruppNamingMode = async () => {
      if (!isOpen || !effectiveOrgID) return;
      try {
        setTruppNamingModeLoading(true);
        const response = await settingsAPI.getTruppNamingMode(effectiveOrgID);
        const mode: 'leader' | 'number' = response?.mode === 'number' ? 'number' : 'leader';
        setTruppNamingMode(mode);
        localStorage.setItem(`truppNamingMode_${effectiveOrgID}`, mode);
        localStorage.setItem('truppNamingMode', mode);
      } catch (error) {
        console.warn('Failed to load trupp naming mode setting:', error);
        const savedOrg = localStorage.getItem(`truppNamingMode_${effectiveOrgID}`);
        const savedGlobal = localStorage.getItem('truppNamingMode');
        const resolved = (savedOrg ?? savedGlobal) === 'number' ? 'number' : 'leader';
        setTruppNamingMode(resolved);
      } finally {
        setTruppNamingModeLoading(false);
      }
    };
    loadTruppNamingMode();
  }, [isOpen, effectiveOrgID]);

  // Load initial data – Cache leeren, damit immer frische Daten geladen werden
  React.useEffect(() => {
    const loadInitialData = async () => {
      // Cache leeren, damit die neusten Daten von der API geladen werden
      clearCache();
      
      try {
        // Load radio channels
        const radioChannels = await syncRadioChannels();
        if (radioChannels) {
          setStandardFunkkanaele(radioChannels.map((c: any) => c.name));
          setRadioChannelsWithIds(radioChannels);
        }

        // Load feedbacks
        const feedbacks = await syncFeedbacks();
        if (feedbacks) {
          setStandardFeedbacks(feedbacks.map((f: any) => f.text));
          setFeedbacksWithIds(feedbacks);
        }

        // Load locations
        const locations = await syncLocations();
        if (locations) {
          setStandardStandorte(locations.map((l: any) => l.name));
          setLocationsWithIds(locations);
        }

        // Load operations types (selected for organization)
        const opsTypes = await syncOperationsTypes();
        if (opsTypes) {
          setOperationsTypes(opsTypes);
        }

        // Load all operations types (available to select)
        const allOpsTypes = await syncAllOperationsTypes();
        if (allOpsTypes) {
          setAllOperationsTypes(allOpsTypes);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
      }
      
      dataLoadedRef.current = true;
    };

    if (isOpen && !dataLoadedRef.current) {
      loadInitialData();
    }
  }, [isOpen]);

  // Load tasks only once when modal opens
  React.useEffect(() => {
    const loadTasks = async () => {
      if (isOpen && !dataLoadedRef.current) {
        try {
          const tasks = await syncTasks();
          if (tasks) {
            setStandardTasks(tasks.map((t: any) => t.name));
            setTasksWithIds(tasks);
          }
        } catch (error) {
          console.error('Error loading tasks:', error);
        }
      }
    };

    loadTasks();
  }, [isOpen]);

  // Save maxTrupps to localStorage
  React.useEffect(() => {
    localStorage.setItem('maxTrupps', String(maxTrupps));
  }, [maxTrupps]);

  // Save standardFunkkanaele to localStorage
  React.useEffect(() => {
    localStorage.setItem('standardFunkkanaele', JSON.stringify(standardFunkkanaele));
  }, [standardFunkkanaele]);

  // Save radioChannelsWithIds to localStorage
  React.useEffect(() => {
    localStorage.setItem('radioChannelsWithIds', JSON.stringify(radioChannelsWithIds));
  }, [radioChannelsWithIds]);

  // Save standardStandorte to localStorage
  React.useEffect(() => {
    localStorage.setItem('standardStandorte', JSON.stringify(standardStandorte));
  }, [standardStandorte]);

  // Save locationsWithIds to localStorage
  React.useEffect(() => {
    localStorage.setItem('locationsWithIds', JSON.stringify(locationsWithIds));
  }, [locationsWithIds]);

  // Save standardTasks to localStorage
  React.useEffect(() => {
    localStorage.setItem('standardTasks', JSON.stringify(standardTasks));
  }, [standardTasks]);

  // Save tasksWithIds to localStorage
  React.useEffect(() => {
    localStorage.setItem('tasksWithIds', JSON.stringify(tasksWithIds));
  }, [tasksWithIds]);

  // Save operationsTypes to localStorage
  React.useEffect(() => {
    localStorage.setItem('operationsTypes', JSON.stringify(operationsTypes));
  }, [operationsTypes]);

  // Functions for radio channels
  const saveStandardFunkkanaele = async () => {
    try {
      const channelName = newFunkkanal.trim();
      if (!channelName) return;

      const success = await addRadioChannelWithCacheClear(channelName);
      if (success) {
        const updatedChannels = await syncRadioChannels();
        if (updatedChannels) {
          setStandardFunkkanaele(updatedChannels.map((c: any) => c.name));
          setRadioChannelsWithIds(updatedChannels);
          setNewFunkkanal('');
        }
      }
    } catch (error) {
      console.error('Error adding radio channel:', error);
    }
  };

  // Functions for feedbacks
  const saveStandardFeedbacks = async () => {
    try {
      const feedbackText = newFeedback.trim();
      if (!feedbackText) return;

      const success = await addFeedbackWithCacheClear(feedbackText);
      if (success) {
        const updatedFeedbacks = await syncFeedbacks();
        if (updatedFeedbacks) {
          setStandardFeedbacks(updatedFeedbacks.map((f: any) => f.text));
          setFeedbacksWithIds(updatedFeedbacks);
          setNewFeedback('');
        }
      }
    } catch (error) {
      console.error('Error adding feedback:', error);
    }
  };

  // Functions for locations
  const saveStandardStandorte = async () => {
    try {
      const locationName = newStandort.trim();
      if (!locationName) return;

      const success = await addLocationWithCacheClear(locationName);
      if (success) {
        const updatedLocations = await syncLocations();
        if (updatedLocations) {
          setStandardStandorte(updatedLocations.map((l: any) => l.name));
          setLocationsWithIds(updatedLocations);
          setNewStandort('');
        }
      }
    } catch (error) {
      console.error('Error adding location:', error);
    }
  };

  // Functions for tasks
  const saveStandardTasks = async () => {
    try {
      const taskName = newTask.trim();
      if (!taskName) return;

      const success = await addTaskWithCacheClear(taskName);
      if (success) {
        const updatedTasks = await syncTasks();
        if (updatedTasks) {
          setStandardTasks(updatedTasks.map((t: any) => t.name));
          setTasksWithIds(updatedTasks);
          setNewTask('');
        }
      }
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  const deleteTask = async (taskID: number) => {
    // Verhindere mehrfache Klicks
    if (deletingTaskIds.has(taskID)) {
      return;
    }
    
    setDeletingTaskIds(prev => new Set([...Array.from(prev), taskID]));
    
    try {
      const success = await deleteTaskWithCacheClear(taskID);
      if (success) {
        const updatedTasks = await syncTasks();
        if (updatedTasks) {
          setStandardTasks(updatedTasks.map((t: any) => t.name));
          setTasksWithIds(updatedTasks);
        }
      }
    } catch (error) {
      console.error('Error removing task:', error);
    } finally {
      setDeletingTaskIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(taskID);
        return newSet;
      });
    }
  };

  // Functions for operations types
  const addOperationsType = async (typeId: number) => {
    try {
      const success = await addOperationsTypeWithCacheClear(typeId);
      if (success) {
        const updatedTypes = await syncOperationsTypes();
        if (updatedTypes) {
          setOperationsTypes(updatedTypes);
        }
      }
    } catch (error) {
      console.error('Error adding operations type:', error);
    }
  };

  const removeOperationsType = async (typeId: number) => {
    try {
      const success = await deleteOperationsTypeWithCacheClear(typeId);
      if (success) {
        const updatedTypes = await syncOperationsTypes();
        if (updatedTypes) {
          setOperationsTypes(updatedTypes);
        }
      }
    } catch (error) {
      console.error('Error removing operations type:', error);
    }
  };

  // Reset data loaded flag when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      dataLoadedRef.current = false;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Gemeinsame Styles für Settings-Boxen
  const settingsBoxStyle: React.CSSProperties = {
    background: '#f8f9fa',
    borderRadius: 12,
    padding: 16,
    height: 'fit-content'
  };

  const settingsLabelStyle: React.CSSProperties = {
    fontWeight: 700,
    marginBottom: 10,
    fontSize: 15,
    color: '#333',
    display: 'flex',
    alignItems: 'center',
    gap: 8
  };

  const inputRowStyle: React.CSSProperties = {
    display: 'flex',
    gap: 8,
    marginBottom: 10
  };

  const inputStyle: React.CSSProperties = {
    fontSize: 14,
    padding: '8px 12px',
    borderRadius: 8,
    border: '1.5px solid #c00',
    outline: 'none',
    flex: 1
  };

  const addButtonStyle: React.CSSProperties = {
    background: '#c00',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 14px',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 16
  };

  const tagContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6
  };

  const tagStyle: React.CSSProperties = {
    background: '#fff',
    borderRadius: 6,
    padding: '5px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    border: '1px solid #ddd',
    fontSize: 13
  };

  const deleteButtonStyle: React.CSSProperties = {
    background: 'none',
    color: '#c00',
    border: 'none',
    fontWeight: 900,
    fontSize: 14,
    marginLeft: 2,
    cursor: 'pointer',
    padding: 0
  };

  return (
    <div style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.5)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      zIndex: 9999 
    }}>
      <div style={{ 
        background: '#fff', 
        borderRadius: 20, 
        padding: '28px 32px', 
        width: 'calc(100vw - 48px)', 
        maxWidth: 1400,
        height: 'calc(100vh - 48px)',
        maxHeight: 900,
        boxShadow: '0 8px 48px rgba(0,0,0,0.2)', 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden' 
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: 24,
          paddingBottom: 16,
          borderBottom: '2px solid #f0f0f0'
        }}>
          <h3 style={{ color: '#c00', fontWeight: 900, fontSize: 24, margin: 0 }}>
            Globale Einstellungen
          </h3>
          <button 
            onClick={() => {
              if (quickTruppCreationLoading) return;
              onClose();
            }} 
            disabled={quickTruppCreationLoading}
            style={{ 
              background: quickTruppCreationLoading ? '#eee' : '#f5f5f5', 
              color: quickTruppCreationLoading ? '#aaa' : '#666', 
              border: 'none', 
              borderRadius: 8, 
              padding: '10px 20px', 
              fontWeight: 700, 
              fontSize: 14, 
              cursor: quickTruppCreationLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = '#eee'}
            onMouseOut={(e) => e.currentTarget.style.background = '#f5f5f5'}
          >
            Schliessen
          </button>
        </div>
        
        {/* Content Grid - 3 Spalten */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: 24, 
          flex: 1, 
          overflow: 'auto',
          paddingRight: 8
        }}>
          
          {/* Spalte 1: Allgemeine Einstellungen + Tasks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            
            {/* Schnell-Erstellung für Trupps */}
            <div style={settingsBoxStyle}>
              <div style={settingsLabelStyle}>
                <span style={{ fontSize: 18 }}>⚡</span>
                Schnell-Erstellung für Trupps
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={quickTruppCreation}
                  disabled={quickTruppCreationLoading}
                  onChange={async (e) => {
                    const newValue = e.target.checked;
                    setQuickTruppCreation(newValue);
                    localStorage.setItem(`quickTruppCreation_${effectiveOrgID}`, newValue.toString());
                    localStorage.setItem('quickTruppCreation', newValue.toString());
                    
                    // Custom Event auslösen, damit andere Komponenten die Änderung mitbekommen
                    window.dispatchEvent(new CustomEvent('quickTruppCreationChanged', { detail: { enabled: newValue } }));
                    
                    try {
                      setQuickTruppCreationLoading(true);
                      if (!effectiveOrgID) throw new Error('Keine gültige Organisation ausgewählt');
                      await settingsAPI.setQuickTruppCreation(effectiveOrgID, newValue);
                    } catch (error) {
                      console.error('Failed to update quick trupp creation setting:', error);
                      setQuickTruppCreation(!newValue);
                      localStorage.setItem(`quickTruppCreation_${effectiveOrgID}`, (!newValue).toString());
                      localStorage.setItem('quickTruppCreation', (!newValue).toString());
                      // Bei Fehler auch Event auslösen mit dem zurückgesetzten Wert
                      window.dispatchEvent(new CustomEvent('quickTruppCreationChanged', { detail: { enabled: !newValue } }));
                    } finally {
                      setQuickTruppCreationLoading(false);
                    }
                  }}
                  style={{ width: 18, height: 18, accentColor: '#c00', marginTop: 2 }}
                />
                <div>
                  <span style={{ fontSize: 13, fontWeight: 500, display: 'block' }}>
                    Trupps ohne Aufgabe, Funkkanal und Standort erstellen
                  </span>
                  <span style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                    Diese Daten müssen dann beim Starten des Trupps eingegeben werden
                  </span>
                </div>
              </label>
            </div>

            {/* Truppnamen-Modus */}
            <div style={settingsBoxStyle}>
              <div style={settingsLabelStyle}>
                <span style={{ fontSize: 18 }}>🏷️</span>
                Truppname
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <select
                  value={truppNamingMode}
                  disabled={truppNamingModeLoading}
                  onChange={async (e) => {
                    const mode = e.target.value === 'number' ? 'number' : 'leader';
                    setTruppNamingMode(mode);
                    localStorage.setItem(`truppNamingMode_${effectiveOrgID}`, mode);
                    localStorage.setItem('truppNamingMode', mode);
                    window.dispatchEvent(new CustomEvent('truppNamingModeChanged', { detail: { mode, orgID: effectiveOrgID } }));
                    try {
                      setTruppNamingModeLoading(true);
                      if (!effectiveOrgID) throw new Error('Keine gültige Organisation ausgewählt');
                      await settingsAPI.setTruppNamingMode(effectiveOrgID, mode);
                    } catch (error) {
                      console.error('Failed to update trupp naming mode setting:', error);
                      const fallbackMode: 'leader' | 'number' = mode === 'number' ? 'leader' : 'number';
                      setTruppNamingMode(fallbackMode);
                      localStorage.setItem(`truppNamingMode_${effectiveOrgID}`, fallbackMode);
                      localStorage.setItem('truppNamingMode', fallbackMode);
                      window.dispatchEvent(new CustomEvent('truppNamingModeChanged', { detail: { mode: fallbackMode, orgID: effectiveOrgID } }));
                    } finally {
                      setTruppNamingModeLoading(false);
                    }
                  }}
                  style={{
                    fontSize: 14,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1.5px solid #c00',
                    outline: 'none',
                    background: '#fff'
                  }}
                >
                  <option value="leader">Nach Truppführer (z.B. Trupp Müller)</option>
                  <option value="number">Nach Nummer (z.B. Trupp 01)</option>
                </select>
                <span style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                  Bei "Nummer" kann der Truppname im Einsatz über das Bearbeiten-Symbol angepasst werden.
                </span>
              </div>
            </div>

            {/* Maximale Anzahl Trupps */}
            <div style={settingsBoxStyle}>
              <div style={settingsLabelStyle}>
                <span style={{ fontSize: 18 }}>👥</span>
                Max. Trupps pro Einsatz
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  onClick={() => setMaxTrupps(Math.max(1, maxTrupps - 1))}
                  style={{ width: 36, height: 36, background: '#c00', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 18, cursor: 'pointer' }}>
                  −
                </button>
                <div style={{
                  fontSize: 18,
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '2px solid #c00',
                  minWidth: 50,
                  textAlign: 'center',
                  fontWeight: 700,
                  background: '#fff'
                }}>
                  {maxTrupps}
                </div>
                <button
                  onClick={() => setMaxTrupps(Math.min(20, maxTrupps + 1))}
                  style={{ width: 36, height: 36, background: '#c00', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 18, cursor: 'pointer' }}>
                  +
                </button>
              </div>
            </div>

            {/* Standard-Tasks */}
            <div style={{ ...settingsBoxStyle, flex: 1 }}>
              <div style={settingsLabelStyle}>
                <span style={{ fontSize: 18 }}>📋</span>
                Standard-Tasks
              </div>
              <div style={inputRowStyle}>
                <input
                  type="text"
                  value={newTask}
                  onChange={e => setNewTask(e.target.value)}
                  placeholder="Neue Task hinzufügen..."
                  style={inputStyle}
                  onKeyDown={e => { if (e.key === 'Enter' && newTask.trim()) { saveStandardTasks(); } }}
                />
                <button onClick={saveStandardTasks} style={addButtonStyle}>+</button>
              </div>
              <div style={tagContainerStyle}>
                {tasksWithIds.map((task, i) => (
                  <span key={task.id || i} style={tagStyle}>
                    {task.name}
                    <button 
                      onClick={() => deleteTask(task.id)} 
                      disabled={deletingTaskIds.has(task.id)}
                      style={{ 
                        ...deleteButtonStyle, 
                        color: deletingTaskIds.has(task.id) ? '#ccc' : '#c00',
                        cursor: deletingTaskIds.has(task.id) ? 'not-allowed' : 'pointer'
                      }} 
                      title={deletingTaskIds.has(task.id) ? "Wird gelöscht..." : "Entfernen"}
                    >
                      {deletingTaskIds.has(task.id) ? '...' : '×'}
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Spalte 2: Funkkanäle + Standorte + Feedbacks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            
            {/* Standard-Funkkanäle */}
            <div style={settingsBoxStyle}>
              <div style={settingsLabelStyle}>
                <span style={{ fontSize: 18 }}>📻</span>
                Standard-Funkkanäle
              </div>
              <div style={inputRowStyle}>
                <input
                  type="text"
                  value={newFunkkanal}
                  onChange={e => setNewFunkkanal(e.target.value)}
                  placeholder="Neuer Funkkanal..."
                  style={inputStyle}
                  onKeyDown={e => { if (e.key === 'Enter' && newFunkkanal.trim()) { saveStandardFunkkanaele(); } }}
                />
                <button onClick={saveStandardFunkkanaele} style={addButtonStyle}>+</button>
              </div>
              <div style={tagContainerStyle}>
                {radioChannelsWithIds.map((channel, i) => (
                  <span key={channel.id || i} style={tagStyle}>
                    {channel.name}
                    <button onClick={async () => {
                      const success = await deleteRadioChannelWithCacheClear(channel.id, channel.name);
                      if (success) {
                        const updatedChannels = await syncRadioChannels();
                        if (updatedChannels) {
                          setStandardFunkkanaele(updatedChannels.map(c => c.name));
                          setRadioChannelsWithIds(updatedChannels);
                        }
                      }
                    }} style={deleteButtonStyle} title="Entfernen">×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Standard-Standorte */}
            <div style={settingsBoxStyle}>
              <div style={settingsLabelStyle}>
                <span style={{ fontSize: 18 }}>📍</span>
                Standard-Standorte
              </div>
              <div style={inputRowStyle}>
                <input
                  type="text"
                  value={newStandort}
                  onChange={e => setNewStandort(e.target.value)}
                  placeholder="Neuer Standort..."
                  style={inputStyle}
                  onKeyDown={e => { if (e.key === 'Enter' && newStandort.trim()) { saveStandardStandorte(); } }}
                />
                <button onClick={saveStandardStandorte} style={addButtonStyle}>+</button>
              </div>
              <div style={tagContainerStyle}>
                {locationsWithIds.map((location, i) => (
                  <span key={location.id || i} style={tagStyle}>
                    {location.name}
                    <button onClick={async () => {
                      const success = await deleteLocationWithCacheClear(location.id, location.name);
                      if (success) {
                        const updatedLocations = await syncLocations();
                        if (updatedLocations) {
                          setStandardStandorte(updatedLocations.map(l => l.name));
                          setLocationsWithIds(updatedLocations);
                        }
                      }
                    }} style={deleteButtonStyle} title="Entfernen">×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Standard-Feedbacks */}
            <div style={{ ...settingsBoxStyle, flex: 1 }}>
              <div style={settingsLabelStyle}>
                <span style={{ fontSize: 18 }}>💬</span>
                Standard-Feedbacks
              </div>
              <div style={inputRowStyle}>
                <input
                  type="text"
                  value={newFeedback}
                  onChange={e => setNewFeedback(e.target.value)}
                  placeholder="Neues Feedback..."
                  style={inputStyle}
                  onKeyDown={e => { if (e.key === 'Enter' && newFeedback.trim()) { saveStandardFeedbacks(); } }}
                />
                <button onClick={saveStandardFeedbacks} style={addButtonStyle}>+</button>
              </div>
              <div style={tagContainerStyle}>
                {feedbacksWithIds.map((feedback, i) => (
                  <span key={feedback.id || i} style={tagStyle}>
                    {feedback.text}
                    <button onClick={async () => {
                      const success = await deleteFeedbackWithCacheClear(feedback.id, feedback.text);
                      if (success) {
                        const updatedFeedbacks = await syncFeedbacks();
                        if (updatedFeedbacks) {
                          setFeedbacksWithIds(updatedFeedbacks);
                        }
                      }
                    }} style={deleteButtonStyle} title="Entfernen">×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Spalte 3: Einsatztypen */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            
            {/* Standard-Einsatztypen */}
            <div style={{ ...settingsBoxStyle, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={settingsLabelStyle}>
                <span style={{ fontSize: 18 }}>🚒</span>
                Standard-Einsatztypen
              </div>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                border: '1px solid #e0e0e0', 
                borderRadius: 10, 
                background: '#fff'
              }}>
                {allOperationsTypes.map((opsType) => {
                  const isSelected = operationsTypes.some(selected => selected.id === opsType.id);
                  return (
                    <div key={opsType.id} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '10px 14px', 
                      borderBottom: '1px solid #f0f0f0',
                      background: isSelected ? '#e8f5e8' : 'transparent',
                      transition: 'background 0.2s'
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 500, color: '#333' }}>{opsType.name}</span>
                      <button 
                        onClick={() => isSelected ? removeOperationsType(opsType.id) : addOperationsType(opsType.id)}
                        style={{ 
                          background: isSelected ? '#e53935' : '#43a047', 
                          color: '#fff', 
                          border: 'none', 
                          borderRadius: 6, 
                          padding: '6px 12px', 
                          fontSize: 12, 
                          fontWeight: 600,
                          cursor: 'pointer',
                          minWidth: 80,
                          transition: 'all 0.2s'
                        }}
                        title={isSelected ? 'Entfernen' : 'Hinzufügen'}
                      >
                        {isSelected ? 'Entfernen' : 'Hinzufügen'}
                      </button>
                    </div>
                  );
                })}
              </div>
              <div style={{ 
                marginTop: 12, 
                padding: '10px 12px', 
                background: '#e8f5e8', 
                borderRadius: 8, 
                fontSize: 12, 
                color: '#2e7d32',
                fontWeight: 500
              }}>
                ✓ {operationsTypes.length} Einsatztypen ausgewählt
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default GlobalSettingsModal;
